import React, { useEffect, useState } from "react";
import { TopBar } from "@/components/TopBar";
import { BottomNav } from "@/components/BottomNav";
import { ProductCard } from "@/components/ProductCard";
import { LoyaltyBanner } from "@/components/LoyaltyBanner";
import { useI18n } from "@/i18n";
import { useAppContext } from "@/state/AppContext";
import { useRouter } from "@/router/Router";
import { fetchLatestReviews, fetchLoyalty, fetchOrders, fetchPosts, fetchRecipes, fetchStories, mediaUrl, type Recipe } from "@/lib/api";
import { BusinessCta, HomeScreenBanner, RecommendationsRail } from "@/components/Growth";
import { RecipeCard } from "@/pages/RecipesPages";
import { inCategory, useCategories } from "@/lib/categories";
import { StoriesRow } from "@/components/Stories";
import { BundleCard, BundleSheet } from "@/components/Bundles";
import { ProductGridSkeleton } from "@/components/Skeleton";
import { ProductImage } from "@/components/ProductImage";
import { useConfig } from "@/lib/useConfig";
import { useCart } from "@/state/CartContext";
import { formatUzs } from "@/lib/format";
import type { Bundle, Order, Story } from "@/types";
import { ReviewCard } from "@/components/ReviewCard";
import { useCatalog } from "@/state/CatalogContext";
import { PostCard } from "@/components/PostCard";
import type { LoyaltyState, PostSummary, Product, ProductCategory, Review } from "@/types";

const HOME_PRODUCTS = 6;

/** Our coffee first, bestsellers and in-stock items ahead within it. */
function homeRank(p: Product): number {
  return (p.category === "coffee" ? 0 : 100) + (p.tags?.includes("bestseller") ? 0 : 10) + (p.variants.some((v) => v.stock > 0) ? 0 : 5);
}

function greetingKey(hour: number): "home.greetingMorning" | "home.greetingDay" | "home.greetingEvening" {
  if (hour < 11) return "home.greetingMorning";
  if (hour < 18) return "home.greetingDay";
  return "home.greetingEvening";
}


export function HomePage() {
  const { t, locale } = useI18n();
  const { user } = useAppContext();
  const { navigate } = useRouter();
  const { products, loading: catalogLoading } = useCatalog();
  const config = useConfig();
  const { theme } = config;
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const { bundles, addItem } = useCart();
  const [stories, setStories] = useState<Story[]>([]);
  const [usual, setUsual] = useState<Order | null>(null);
  const [openBundle, setOpenBundle] = useState<Bundle | null>(null);
  const tx = (key: "announcement" | "eyebrow" | "title1" | "title2" | "text", fallback: string) => theme?.[key]?.[locale]?.trim() || theme?.[key]?.ru?.trim() || fallback;
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewsTotal, setReviewsTotal] = useState(0);
  // Tabs come from the categories managed in the admin (only those with products).
  const allCategories = useCategories();
  const categories = (allCategories ?? []).filter((c) => products.some((p) => inCategory(p, c.slug)));
  const [picked, setHomeCategory] = useState<string | null>(null);
  const homeCategory = picked && categories.some((c) => c.slug === picked) ? picked : categories[0]?.slug ?? "coffee";
  const homeProducts = [...products]
    .filter((p) => inCategory(p, homeCategory))
    .sort((a, b) => homeRank(a) - homeRank(b))
    .slice(0, HOME_PRODUCTS);
  const [loyalty, setLoyalty] = useState<LoyaltyState | null>(null);
  const [promos, setPromos] = useState<PostSummary[]>([]);
  const [news, setNews] = useState<PostSummary[]>([]);

  useEffect(() => {
    fetchStories().then(setStories).catch(() => setStories([]));
    fetchRecipes().then(setRecipes).catch(() => setRecipes([]));
    // "Как обычно": the customer's most recent completed purchase.
    fetchOrders()
      .then((orders) => setUsual(orders.find((o) => o.status !== "cancelled" && o.paymentStatus === "paid") ?? null))
      .catch(() => setUsual(null));
    fetchLatestReviews(3)
      .then((r) => {
        setReviews(r.items);
        setReviewsTotal(r.total);
      })
      .catch(() => {});
    fetchLoyalty().then(setLoyalty).catch(() => {});
    fetchPosts({ type: "promo", home: true, limit: 6 }).then(setPromos).catch(() => {});
    fetchPosts({ limit: 3 })
      .then((list) => setNews(list.filter((p) => p.type !== "promo")))
      .catch(() => {});
  }, [locale]);

  return (
    <>
      <div className="announcement">{tx("announcement", t("home.announcement"))}</div>
      <TopBar title="KMS ROASTING" brand />
      <main className="page">
        <div className="home-intro">
          <span className="eyebrow">KMS ROASTING · {t("home.city")}</span>
          <span className="greeting">{t(greetingKey(new Date().getHours()), { name: user?.firstName ?? t("profile.guest") })}</span>
        </div>

        <HomeScreenBanner />
        {config.exhibition && <BusinessCta />}
        <StoriesRow stories={stories} />

        <section className="hero" style={theme ? ({ "--hero-bg": theme.heroBg, "--hero-art-bg": theme.heroArtBg } as React.CSSProperties) : undefined}>
          <div className="hero__copy">
            <span className="eyebrow">{tx("eyebrow", t("home.heroEyebrow"))}</span>
            <h1 className="hero__title">
              {tx("title1", t("home.heroTitle1"))}
              <br />
              <em>{tx("title2", t("home.heroTitle2"))}</em>
            </h1>
            <p className="hero__text">{tx("text", t("home.heroText"))}</p>
            <button className="hero__cta" onClick={() => navigate({ name: "catalog", category: "coffee" })}>
              {t("home.heroCta")}
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path d="M7 17 17 7M9 7h8v8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
          {theme?.heroImage ? (
            <div className="hero__art hero__art--photo">
              <img src={mediaUrl(theme.heroImage)} alt="" />
            </div>
          ) : (
            <div className="hero__art">
              <img src="/brand/hero-pack.webp" alt="" />
              <div className="hero__stamp">
                ROASTED WITH CARE
                <br />
                TASHKENT
              </div>
            </div>
          )}
        </section>

        <div className="service-row">
          <span>
            <ServiceIcon kind="bag" />
            {t("home.servicePack")}
          </span>
          <span>
            <ServiceIcon kind="truck" />
            {t("home.serviceDelivery")}
          </span>
          <span>
            <ServiceIcon kind="clock" />
            {t("home.servicePickup")}
          </span>
        </div>

        {usual && usual.items.length > 0 && <UsualOrder order={usual} image={products.find((p) => p.id === usual.items[0].productId)?.images[0]} onRepeat={() => {
          const available = usual.items.filter((i) => products.some((p) => p.id === i.productId && p.variants.some((v) => v.id === i.variantId && v.stock > 0)));
          available.forEach((i) => addItem(i.productId, i.variantId, i.quantity));
          navigate({ name: "cart" });
        }} />}

        {loyalty && <LoyaltyBanner loyalty={loyalty} />}

        {promos.length > 0 && (
          <>
            <div className="section-head">
              <div>
                <span className="eyebrow">KMS</span>
                <h2>{t("home.promosTitle")}</h2>
              </div>
            </div>
            <div className="rail">
              {promos.map((p) => (
                <div key={p.id} style={{ width: 250, flexShrink: 0 }}>
                  <PostCard post={p} compact />
                </div>
              ))}
            </div>
          </>
        )}

        <RecommendationsRail />

        <div className="section-head">
          <div>
            <span className="eyebrow">{t("home.discover")}</span>
            <h2>{t("home.collectionTitle")}</h2>
          </div>
        </div>
        {categories.length > 1 && (
          <div className="category-tabs">
            {categories.map((c) => (
              <button key={c.id} data-active={c.slug === homeCategory} onClick={() => setHomeCategory(c.slug)}>
                {c.name}
              </button>
            ))}
          </div>
        )}
        {catalogLoading && products.length === 0 ? (
          <ProductGridSkeleton count={4} />
        ) : (
          <div className="grid-2">
            {homeProducts.map((p) => (
              <ProductCard key={p.id} product={p} width="100%" />
            ))}
          </div>
        )}
        <button className="btn btn--ghost" style={{ width: "100%", marginTop: "var(--space-5)" }} onClick={() => navigate({ name: "catalog", category: homeCategory })}>
          {t("home.seeMore")}
        </button>

        {!config.exhibition && (
          <div style={{ marginTop: "var(--space-6)" }}>
            <BusinessCta />
          </div>
        )}

        {recipes.length > 0 && (
          <>
            <div className="section-head">
              <div>
                <span className="eyebrow">{t("recipes.eyebrow")}</span>
                <h2>{t("recipes.title")}</h2>
              </div>
              <button className="link-button" onClick={() => navigate({ name: "recipes" })}>
                {t("home.seeAll")}
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {recipes.slice(0, 2).map((r) => (
                <RecipeCard key={r.id} r={r} />
              ))}
            </div>
          </>
        )}

        {config.features.events && (
          <button className="card biz-row" style={{ marginTop: "var(--space-5)" }} onClick={() => navigate({ name: "events" })}>
            <span>
              <b>{t("events.homeTitle")}</b>
              <small>{t("events.homeText")}</small>
            </span>
            <span aria-hidden="true">›</span>
          </button>
        )}

        {bundles.length > 0 && (
          <>
            <div className="section-head">
              <div>
                <span className="eyebrow">{t("bundles.eyebrowHome")}</span>
                <h2>{t("bundles.title")}</h2>
              </div>
            </div>
            <div className="rail">
              {bundles.map((b) => (
                <BundleCard key={b.id} bundle={b} onOpen={() => setOpenBundle(b)} />
              ))}
            </div>
          </>
        )}

        {reviews.length > 0 && (
          <>
            <div className="section-head">
              <div>
                <span className="eyebrow">{t("home.reviewsEyebrow")}</span>
                <h2>{t("home.reviewsTitle")}</h2>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {reviews.map((r) => (
                <ReviewCard key={r.id} review={r} showProduct />
              ))}
            </div>
            {reviewsTotal > reviews.length && (
              <button className="btn btn--ghost" style={{ width: "100%", marginTop: "var(--space-3)" }} onClick={() => navigate({ name: "reviews" })}>
                {t("reviews.all", { count: reviewsTotal })}
              </button>
            )}
          </>
        )}

        {news.length > 0 && (
          <>
            <div className="section-head">
              <div>
                <span className="eyebrow">KMS JOURNAL</span>
                <h2>{t("home.newsTitle")}</h2>
              </div>
              <button className="link-button" onClick={() => navigate({ name: "posts" })}>
                {t("home.seeAll")}
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {news.map((p) => (
                <PostCard key={p.id} post={p} />
              ))}
            </div>
          </>
        )}

        <section className="brand-panel">
          <span className="eyebrow">KMS ROASTING</span>
          <h3>{t("home.aboutCoffeeTitle")}</h3>
          <p>{t("home.aboutCoffeeBody")}</p>
        </section>

        <footer className="store-footer">
          <img src="/brand/kms-logo.png" alt="KMS ROASTING" />
          <div>
            {t("checkout.pickupAddress")}
            <br />
            {t("checkout.pickupHours")}
          </div>
        </footer>
      </main>
      <BottomNav />
      <BundleSheet bundle={openBundle} onClose={() => setOpenBundle(null)} />
    </>
  );
}

/** "Как обычно": repeat the last purchase in one tap. */
function UsualOrder({ order, image, onRepeat }: { order: Order; image?: string; onRepeat: () => void }) {
  const { t, locale } = useI18n();
  const first = order.items[0];
  const more = order.items.length - 1;
  return (
    <div className="usual">
      <div className="usual__img">
        <ProductImage src={image} alt="" className="product-card__image" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span className="eyebrow">{t("home.usualTitle")}</span>
        <div className="usual__name">
          {first.productName}
          {first.variantLabel !== "Стандарт" ? ` · ${first.variantLabel}` : ""} × {first.quantity}
          {more > 0 && <span className="usual__more"> {t("home.usualMore", { count: more })}</span>}
        </div>
        <div className="usual__meta">{formatUzs(order.totalUzs, locale)}</div>
      </div>
      <button className="btn btn--primary usual__btn" onClick={onRepeat}>
        {t("home.usualRepeat")}
      </button>
    </div>
  );
}

function ServiceIcon({ kind }: { kind: "bag" | "truck" | "clock" }) {
  const common = { stroke: "currentColor", strokeWidth: 1.3, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, fill: "none" };
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
      {kind === "bag" && (
        <>
          <path {...common} d="M6 7h12l-1 13H7L6 7Z" />
          <path {...common} d="M9 7V5.5a3 3 0 0 1 6 0V7" />
        </>
      )}
      {kind === "truck" && (
        <>
          <path {...common} d="M3 6h11v10H3zM14 10h4l3 3v3h-7" />
          <circle {...common} cx="7" cy="17.5" r="1.8" />
          <circle {...common} cx="17" cy="17.5" r="1.8" />
        </>
      )}
      {kind === "clock" && (
        <>
          <circle {...common} cx="12" cy="12" r="8.5" />
          <path {...common} d="M12 7.5V12l3 2" />
        </>
      )}
    </svg>
  );
}
